import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
    Activity,
    AlertCircle,
    ArrowLeft,
    ArrowRight,
    CheckCircle2,
    ExternalLink,
    Eye,
    EyeOff,
    Lock,
    LogIn,
    Mail,
    RefreshCw,
    ShieldAlert,
    ShieldCheck,
    Sparkles,
    User as UserIcon,
    UserPlus,
    Zap,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { useAuth } from '../auth/AuthContext'
import { ThemeToggle } from '../components/header/ThemeToggle'
import { LOGIN_PAGE_CONFIG } from '../config/constants'
import { usePageTitle } from '../hooks/usePageTitle'
import { UserStatus } from '../types'

export const Route = createFileRoute('/login')({
    component: LoginRoute,
    validateSearch: (search: Record<string, unknown>) => {
        const rawRedirect = search.redirect
        const redirect = typeof rawRedirect === 'string' ? rawRedirect : '/home'
        return { redirect }
    },
})

function LoginRoute() {
    usePageTitle('Terminal Access // Login')
    const navigate = useNavigate()
    const {
        isAuthenticated,
        isAdmin,
        signInWithGoogle,
        signInWithGoogleRedirect,
        signInWithEmail,
        signUpWithEmail,
        signOut,
        loading,
        userData,
    } = useAuth()

    const [isSigningIn, setIsSigningIn] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [isSignUp, setIsSignUp] = useState(false)
    const [showPassword, setShowPassword] = useState(false)

    // Form inputs
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [displayName, setDisplayName] = useState('')

    const { redirect } = Route.useSearch()

    // Redirect if authenticated and approved
    useEffect(() => {
        if (!loading && isAuthenticated && (userData?.status === UserStatus.ACTIVE || isAdmin)) {
            navigate({
                to: redirect,
                replace: true,
            })
        }
    }, [loading, isAuthenticated, userData, isAdmin, navigate, redirect])

    const handleGoogleSignIn = async () => {
        if (isSigningIn || loading) return
        try {
            setIsSigningIn(true)
            setError(null)

            const result = await signInWithGoogle()

            if (result.success && result.pending) {
                return
            }

            if (!result.success) {
                if (
                    result.error?.includes('popup-closed-by-user') ||
                    result.error?.includes('cancelled')
                ) {
                    setError('Authentication cancelled. Please complete the Google sign-in prompt.')
                } else {
                    setError(result.error || 'Google authentication failed. Please try again.')
                }
                setIsSigningIn(false)
            }
        } catch (err: any) {
            console.error('Google sign-in error:', err)
            setError(err.message || 'Google sign-in failed')
            setIsSigningIn(false)
        }
    }

    const handleEmailAuth = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!email.trim() || !password) {
            setError('Please enter your email address and password.')
            return
        }

        if (isSignUp && !displayName.trim()) {
            setError('Please enter your full name for access clearance.')
            return
        }

        try {
            setIsSigningIn(true)
            setError(null)

            const result = isSignUp
                ? await signUpWithEmail(email.trim(), password, displayName.trim())
                : await signInWithEmail(email.trim(), password)

            if (result.success && result.user && result.userData) {
                if (
                    result.userData.status !== UserStatus.ACTIVE &&
                    result.userData.role !== 'admin'
                ) {
                    setError('Account registration recorded. Administrator clearance is required.')
                } else {
                    navigate({
                        to: redirect,
                        replace: true,
                    })
                }
            } else {
                setError(result.error || `${isSignUp ? 'Registration' : 'Authentication'} failed.`)
            }
        } catch (err: any) {
            console.error('Email auth error:', err)
            setError(err.message || `${isSignUp ? 'Registration' : 'Authentication'} failed.`)
        } finally {
            setIsSigningIn(false)
        }
    }

    // --- State: Account Awaiting Admin Approval ---
    if (isAuthenticated && userData) {
        const isPendingOrRejected =
            userData.status !== UserStatus.ACTIVE && userData.role !== 'admin'

        if (isPendingOrRejected) {
            return (
                <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-100/70 dark:from-[#050909] dark:via-[#080d0d] dark:to-[#050909] text-slate-900 dark:text-slate-100 flex flex-col justify-between items-center p-4 sm:p-6 relative overflow-hidden font-sans selection:bg-[#005451]/15 selection:text-[#005451] dark:selection:bg-[#005451]/40 dark:selection:text-teal-200 transition-colors duration-300">
                    {/* Top Bar with Theme Toggle */}
                    <div className="relative z-20 flex items-center justify-between max-w-md w-full mb-4">
                        <button
                            type="button"
                            onClick={() => navigate({ to: '/' })}
                            className="group inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800 border border-slate-200/90 dark:border-white/10 text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white text-xs font-semibold shadow-xs hover:shadow-md transition-all"
                        >
                            <ArrowLeft size={13} className="group-hover:-translate-x-0.5 transition-transform text-[#005451] dark:text-teal-400" />
                            <span>Return to Home</span>
                        </button>
                        <ThemeToggle />
                    </div>

                    {/* Ambient Glows */}
                    <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[450px] h-[450px] bg-amber-500/10 rounded-full blur-[140px] pointer-events-none" />
                    <div className="absolute bottom-10 left-1/3 w-[400px] h-[400px] bg-emerald-500/10 dark:bg-[#005451]/20 rounded-full blur-[140px] pointer-events-none" />

                    <div className="w-full max-w-md bg-white/95 dark:bg-[#0a1111]/95 backdrop-blur-2xl border border-slate-200/90 dark:border-white/10 rounded-3xl p-6 sm:p-7 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.08)] dark:shadow-[0_25px_70px_-15px_rgba(0,0,0,0.85)] text-center relative z-10 space-y-4 my-auto transition-all duration-300">
                        {/* Status Icon */}
                        <div className="w-14 h-14 rounded-2xl bg-amber-50 dark:bg-amber-500/15 border border-amber-200 dark:border-amber-500/30 text-amber-600 dark:text-amber-400 flex items-center justify-center mx-auto shadow-md shadow-amber-500/10">
                            <ShieldAlert size={28} />
                        </div>

                        {/* Status Heading */}
                        <div className="space-y-1.5">
                            <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 text-amber-800 dark:text-amber-300 text-[10px] font-mono font-bold uppercase tracking-wider">
                                Clearance Status: Pending Approval
                            </div>
                            <h2 className="text-xl sm:text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white">
                                Verification in Progress
                            </h2>
                            <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed font-medium">
                                To safeguard proprietary algorithms and data feeds, all newly registered accounts require manual Administrator approval prior to platform access.
                            </p>
                        </div>

                        {/* User Identity Details Card */}
                        <div className="p-3.5 rounded-xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200/80 dark:border-white/5 text-left font-mono text-xs space-y-2">
                            <div className="flex justify-between items-center text-slate-500 dark:text-slate-400">
                                <span className="text-[10px]">ACCOUNT IDENTIFIER</span>
                                <span className="text-slate-900 dark:text-white font-bold">{userData.displayName || 'Authorized User'}</span>
                            </div>
                            <div className="flex justify-between items-center text-slate-500 dark:text-slate-400">
                                <span className="text-[10px]">WORK EMAIL</span>
                                <span className="text-[#005451] dark:text-teal-300 font-bold truncate max-w-[200px]">{userData.email}</span>
                            </div>
                            <div className="flex justify-between items-center text-slate-500 dark:text-slate-400">
                                <span className="text-[10px]">PLATFORM ROLE</span>
                                <span className="px-2 py-0.5 rounded bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-[10px] font-bold">USER (PENDING REVIEW)</span>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="pt-1 flex flex-col sm:flex-row gap-2.5">
                            <button
                                type="button"
                                onClick={() => window.location.reload()}
                                className="flex-1 py-2.5 px-4 rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-white/10 dark:hover:bg-white/15 border border-slate-200 dark:border-white/15 text-slate-900 dark:text-white font-bold text-xs uppercase tracking-wider flex items-center justify-center gap-2 transition-all shadow-xs active:scale-[0.99]"
                            >
                                <RefreshCw size={13} /> Check Clearance
                            </button>
                            <button
                                type="button"
                                onClick={() => signOut()}
                                className="flex-1 py-2.5 px-4 rounded-xl bg-slate-200 hover:bg-slate-300 dark:bg-slate-800 dark:hover:bg-slate-700 border border-slate-300 dark:border-white/10 text-slate-700 dark:text-slate-300 font-bold text-xs uppercase tracking-wider transition-all active:scale-[0.99]"
                            >
                                Sign Out
                            </button>
                        </div>
                    </div>

                    <div className="text-[10px] font-mono text-slate-400 dark:text-slate-500">
                        OPTIONS ORACLE // CLEARANCE CONTROL
                    </div>
                </div>
            )
        }

        // Active user waiting for automatic route navigation
        return (
            <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-100/70 dark:from-[#050909] dark:via-[#080d0d] dark:to-[#050909] text-slate-900 dark:text-slate-100 flex flex-col justify-center items-center p-6 font-sans">
                <div className="w-10 h-10 rounded-full border-2 border-[#005451] border-t-teal-400 animate-spin mb-3" />
                <div className="text-xs font-mono font-bold text-[#005451] dark:text-teal-300 tracking-wider uppercase animate-pulse">
                    Securing Terminal Session...
                </div>
            </div>
        )
    }

    // --- State: Standard Login / Registration Gateway (Ultra-Premium Redesign) ---
    return (
        <div className="min-h-screen lg:h-screen lg:max-h-screen flex flex-col justify-between p-3 sm:p-5 lg:p-6 bg-gradient-to-b from-slate-50 via-white to-slate-100/70 dark:from-[#050909] dark:via-[#080d0d] dark:to-[#050909] text-slate-900 dark:text-slate-100 relative overflow-x-hidden overflow-y-auto lg:overflow-hidden font-sans selection:bg-[#005451]/15 selection:text-[#005451] dark:selection:bg-[#005451]/40 dark:selection:text-teal-200 transition-colors duration-300">
            {/* Ambient Background Atmospheric Glows */}
            <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
                <div className="absolute -top-32 -left-32 w-[500px] h-[500px] bg-gradient-to-br from-emerald-200/40 via-teal-100/30 to-transparent dark:from-[#005451]/30 dark:via-teal-900/15 dark:to-transparent rounded-full blur-[140px]" />
                <div className="absolute top-1/2 -right-32 w-[480px] h-[480px] bg-gradient-to-bl from-teal-200/30 via-emerald-100/20 to-transparent dark:from-teal-600/15 dark:via-[#003836]/20 dark:to-transparent rounded-full blur-[150px]" />
                <div className="absolute -bottom-32 left-1/3 w-[550px] h-[550px] bg-slate-200/40 dark:bg-[#002f2d]/20 rounded-full blur-[160px]" />
                <div className="absolute inset-0 bg-[radial-gradient(#e2e8f0_1px,transparent_1px)] dark:bg-[radial-gradient(rgba(255,255,255,0.04)_1px,transparent_1px)] [background-size:24px_24px] opacity-70" />
            </div>

            {/* Top Navigation Bar with Theme Toggle */}
            <div className="relative z-20 flex items-center justify-between max-w-7xl mx-auto w-full mb-3 sm:mb-4">
                <button
                    type="button"
                    onClick={() => navigate({ to: '/' })}
                    className="group inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/80 dark:bg-slate-900/80 hover:bg-white dark:hover:bg-slate-900 border border-slate-200/90 dark:border-white/10 text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white text-xs font-semibold shadow-xs hover:shadow-md transition-all"
                >
                    <ArrowLeft size={13} className="group-hover:-translate-x-0.5 transition-transform text-[#005451] dark:text-teal-400" />
                    <span>Back to Overview</span>
                </button>

                <div className="flex items-center gap-2.5">
                    <div className="hidden sm:inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/80 dark:bg-slate-900/80 border border-slate-200/90 dark:border-white/10 text-slate-700 dark:text-slate-300 text-[10px] font-mono font-medium shadow-xs">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                        </span>
                        <span>{LOGIN_PAGE_CONFIG.GATEWAY_TITLE} • {LOGIN_PAGE_CONFIG.GATEWAY_SUBTITLE}</span>
                    </div>
                    {/* Theme Toggle Button */}
                    <ThemeToggle />
                </div>
            </div>

            {/* Main Interactive Grid */}
            <div className="relative z-10 max-w-6xl mx-auto w-full my-auto grid md:grid-cols-12 gap-6 md:gap-8 lg:gap-12 items-center">
                {/* Left Desk Telemetry Panel (Visible on MD and larger: Tablets, Laptops & Desktops) */}
                <div className="hidden md:flex md:col-span-6 lg:col-span-6 flex-col space-y-4 pr-2 lg:pr-4">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#005451] via-[#006e6a] to-[#004240] flex items-center justify-center text-white shadow-lg shadow-[#005451]/30">
                            <Activity size={20} strokeWidth={2.5} />
                        </div>
                        <div>
                            <span className="text-lg font-black tracking-tight text-slate-900 dark:text-white">
                                Options<span className="text-[#005451] dark:text-teal-400">Oracle</span>
                            </span>
                            <div className="text-[9px] font-mono font-bold tracking-widest uppercase text-slate-400 dark:text-slate-500">
                                QUANTITATIVE TRADING DESK
                            </div>
                        </div>
                    </div>

                    <div className="space-y-1.5">
                        <h1 className="text-2xl lg:text-3xl font-black text-slate-900 dark:text-white tracking-tight leading-tight">
                            {LOGIN_PAGE_CONFIG.HERO_TITLE} <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#005451] via-[#007a76] to-[#00a8a1] dark:from-teal-300 dark:via-teal-400 dark:to-emerald-400">
                                {LOGIN_PAGE_CONFIG.HERO_ACCENT}
                            </span>
                        </h1>
                        <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed font-medium max-w-md">
                            {LOGIN_PAGE_CONFIG.HERO_DESCRIPTION}
                        </p>
                    </div>

                    {/* Live Benchmark Indices Ticker Chips */}
                    <div className="grid grid-cols-3 gap-2 pt-1">
                        {LOGIN_PAGE_CONFIG.INDICES.map((item, idx) => (
                            <div
                                key={idx}
                                className="p-2.5 rounded-xl bg-white/80 dark:bg-slate-900/60 border border-slate-200/80 dark:border-white/10 shadow-xs flex flex-col justify-between"
                            >
                                <div className="flex items-center justify-between">
                                    <span className="text-[10px] font-mono font-bold text-slate-700 dark:text-slate-300">{item.symbol}</span>
                                    <span className="text-[9px] font-mono text-emerald-600 dark:text-emerald-400 font-bold">{item.change}</span>
                                </div>
                                <div className="text-xs font-mono font-bold text-slate-900 dark:text-white mt-1">
                                    {item.price}
                                </div>
                                <div className="text-[9px] font-mono text-slate-400 dark:text-slate-500 mt-0.5">
                                    Step: {item.step}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Security & Feature Badges */}
                    <div className="space-y-2 pt-1">
                        {LOGIN_PAGE_CONFIG.FEATURES.map((item, idx) => (
                            <div
                                key={idx}
                                className="p-2.5 rounded-xl bg-white/80 dark:bg-white/[0.03] border border-slate-200/80 dark:border-white/[0.07] shadow-xs flex items-center gap-3 transition-colors"
                            >
                                <div className="w-5 h-5 rounded-md bg-emerald-50 dark:bg-[#005451]/40 border border-emerald-200 dark:border-[#005451] flex items-center justify-center text-[#005451] dark:text-teal-300 shrink-0">
                                    <CheckCircle2 size={12} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-xs font-bold text-slate-900 dark:text-slate-200 truncate">{item.title}</div>
                                    <div className="text-[10px] text-slate-500 dark:text-slate-400 font-medium truncate">{item.desc}</div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Telemetry Strip */}
                    <div className="pt-1 flex items-center gap-2 text-[10px] font-mono font-bold text-slate-500 dark:text-slate-400">
                        {LOGIN_PAGE_CONFIG.TELEMETRY.map((tel, idx) => (
                            <span key={idx} className="px-2 py-0.5 rounded-md bg-white/80 dark:bg-slate-900/60 border border-slate-200/80 dark:border-white/10 shadow-xs">
                                {tel.label}: <span className="text-slate-800 dark:text-slate-200">{tel.value}</span>
                            </span>
                        ))}
                    </div>
                </div>

                {/* Right Authentication Console */}
                <div className="col-span-12 md:col-span-6 lg:col-span-6 w-full max-w-[420px] mx-auto">
                    {/* Mobile Brand Banner (< md screens only) */}
                    <div className="md:hidden flex items-center justify-between p-2.5 rounded-2xl bg-white/80 dark:bg-slate-900/80 border border-slate-200/80 dark:border-white/10 shadow-xs mb-3">
                        <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#005451] via-[#006e6a] to-[#004240] flex items-center justify-center text-white shadow-xs">
                                <Activity size={14} />
                            </div>
                            <span className="text-xs font-black text-slate-900 dark:text-white">
                                Options<span className="text-[#005451] dark:text-teal-400">Oracle</span>
                            </span>
                        </div>
                        <div className="flex items-center gap-1.5 text-[9px] font-mono font-bold text-emerald-600 dark:text-teal-400">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                            <span>DESK // PRO V2.0</span>
                        </div>
                    </div>

                    {/* Multi-layered Glass Frame */}
                    <div className="relative rounded-[28px] p-[1.5px] bg-gradient-to-b from-[#005451]/35 via-teal-500/25 to-[#005451]/15 dark:from-teal-400/35 dark:via-white/10 dark:to-teal-500/15 shadow-[0_22px_55px_-12px_rgba(0,84,81,0.18),0_0_35px_-5px_rgba(0,180,160,0.12)] dark:shadow-[0_25px_70px_-15px_rgba(0,0,0,0.9),0_0_40px_-10px_rgba(0,84,81,0.35)] transition-all duration-300">
                        <div className="rounded-[26.5px] bg-white/95 dark:bg-[#071010]/95 backdrop-blur-2xl p-5 sm:p-6 border border-white/80 dark:border-white/10 overflow-hidden relative space-y-3.5 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.9)] dark:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)]">
                            
                            {/* Window Chrome Top Header */}
                            <div className="flex items-center justify-between border-b border-slate-100 dark:border-white/10 pb-2.5">
                                <div className="flex items-center gap-2">
                                    <div className="flex items-center gap-1.5">
                                        <span className="w-2.5 h-2.5 rounded-full bg-rose-400 dark:bg-rose-500/80 shadow-[0_0_6px_rgba(244,63,94,0.35)]" />
                                        <span className="w-2.5 h-2.5 rounded-full bg-amber-400 dark:bg-amber-500/80 shadow-[0_0_6px_rgba(245,158,11,0.35)]" />
                                        <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 dark:bg-emerald-500/80 shadow-[0_0_6px_rgba(16,185,129,0.35)]" />
                                    </div>
                                    <span className="text-[10px] font-mono font-bold tracking-widest text-slate-400 dark:text-slate-500 uppercase ml-1">
                                        {LOGIN_PAGE_CONFIG.AUTH_HEADER}
                                    </span>
                                </div>

                                <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-[#005451]/30 border border-emerald-200 dark:border-[#005451]/60 text-emerald-800 dark:text-teal-400 text-[10px] font-mono font-bold uppercase tracking-wider">
                                    <Sparkles size={10} /> {LOGIN_PAGE_CONFIG.VERSION_BADGE}
                                </div>
                            </div>

                            {/* Luxury Segmented Mode Switcher */}
                            <div className="grid grid-cols-2 p-1 rounded-xl bg-slate-100/90 dark:bg-slate-950/80 border border-slate-200/70 dark:border-white/10">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setIsSignUp(false)
                                        setError(null)
                                    }}
                                    className={`flex items-center justify-center gap-1.5 py-1.5 rounded-lg font-bold text-xs transition-all duration-200 ${
                                        !isSignUp
                                            ? 'bg-white dark:bg-[#005451] text-slate-900 dark:text-white shadow-xs'
                                            : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                                    }`}
                                >
                                    <LogIn size={13} />
                                    <span>Sign In</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setIsSignUp(true)
                                        setError(null)
                                    }}
                                    className={`flex items-center justify-center gap-1.5 py-1.5 rounded-lg font-bold text-xs transition-all duration-200 ${
                                        isSignUp
                                            ? 'bg-white dark:bg-[#005451] text-slate-900 dark:text-white shadow-xs'
                                            : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                                    }`}
                                >
                                    <UserPlus size={13} />
                                    <span>Request Clearance</span>
                                </button>
                            </div>

                            {/* Heading */}
                            <div className="space-y-0.5">
                                <h2 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white tracking-tight">
                                    {isSignUp ? LOGIN_PAGE_CONFIG.TITLE_SIGN_UP : LOGIN_PAGE_CONFIG.TITLE_SIGN_IN}
                                </h2>
                                <p className="text-xs text-slate-500 dark:text-slate-400 font-medium leading-normal">
                                    {isSignUp
                                        ? LOGIN_PAGE_CONFIG.SUBTITLE_SIGN_UP
                                        : LOGIN_PAGE_CONFIG.SUBTITLE_SIGN_IN}
                                </p>
                            </div>

                            {/* Error Alert Box */}
                            {error && (
                                <div className="p-2.5 rounded-xl bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/30 text-rose-700 dark:text-rose-300 text-xs font-medium flex items-center gap-2 animate-fade-in shadow-xs">
                                    <AlertCircle size={14} className="shrink-0 text-rose-500 dark:text-rose-400" />
                                    <span className="leading-snug text-[11px]">{error}</span>
                                </div>
                            )}

                            {/* Single-Click Google SSO Button */}
                            <div className="space-y-1.5">
                                <button
                                    type="button"
                                    onClick={handleGoogleSignIn}
                                    disabled={isSigningIn || loading}
                                    className="w-full relative flex items-center justify-center gap-2.5 py-2.5 px-4 rounded-xl bg-white dark:bg-slate-900/90 hover:bg-slate-50 dark:hover:bg-slate-800/90 border border-slate-200/90 dark:border-white/10 hover:border-slate-300 dark:hover:border-white/20 text-slate-800 dark:text-slate-100 font-bold text-xs sm:text-sm shadow-xs hover:shadow-sm transition-all duration-200 group active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <svg className="w-4 h-4 group-hover:scale-105 transition-transform shrink-0" viewBox="0 0 24 24">
                                        <path
                                            fill="#4285F4"
                                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                        />
                                        <path
                                            fill="#34A853"
                                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                        />
                                        <path
                                            fill="#FBBC05"
                                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                        />
                                        <path
                                            fill="#EA4335"
                                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                        />
                                    </svg>
                                    <span>{isSigningIn ? 'Connecting...' : LOGIN_PAGE_CONFIG.GOOGLE_BUTTON_TEXT}</span>
                                </button>

                                {/* Compact Pop-up Helper with ExternalLink Icon */}
                                <div className="text-center">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setIsSigningIn(true)
                                            signInWithGoogleRedirect()
                                        }}
                                        disabled={isSigningIn || loading}
                                        className="inline-flex items-center gap-1 text-[10px] text-slate-400 dark:text-slate-500 hover:text-[#005451] dark:hover:text-teal-400 transition-colors"
                                    >
                                        <span>{LOGIN_PAGE_CONFIG.POPUP_REDIRECT_TEXT}</span>
                                        <ExternalLink size={10} />
                                    </button>
                                </div>
                            </div>

                            {/* Refined Modern Hairline Divider */}
                            <div className="relative my-0.5">
                                <div className="absolute inset-0 flex items-center">
                                    <div className="w-full border-t border-slate-200/80 dark:border-white/10" />
                                </div>
                                <div className="relative flex justify-center text-[10px] font-mono uppercase tracking-widest text-slate-400 dark:text-slate-500">
                                    <span className="px-2.5 bg-white dark:bg-[#071010]">
                                        or continue with credentials
                                    </span>
                                </div>
                            </div>

                            {/* Email/Password Form */}
                            <form onSubmit={handleEmailAuth} className="space-y-3">
                                {isSignUp && (
                                    <div className="space-y-1">
                                        <label className="block text-[11px] font-mono font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400">
                                            Trader Full Name
                                        </label>
                                        <div className="relative">
                                            <UserIcon
                                                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500"
                                                size={15}
                                            />
                                            <input
                                                type="text"
                                                value={displayName}
                                                onChange={(e) => setDisplayName(e.target.value)}
                                                className="w-full pl-9 pr-4 py-2 bg-slate-50/80 dark:bg-slate-950/70 hover:bg-slate-100/60 dark:hover:bg-slate-950/90 focus:bg-white dark:focus:bg-slate-950 border border-slate-200/80 dark:border-white/10 focus:border-[#005451] dark:focus:border-teal-500 focus:ring-2 focus:ring-[#005451]/15 dark:focus:ring-teal-500/20 rounded-xl text-slate-900 dark:text-white text-xs sm:text-sm placeholder:text-slate-400 dark:placeholder:text-slate-600 transition-all outline-none font-medium"
                                                placeholder="e.g. Sarah Jenkins"
                                                disabled={isSigningIn || loading}
                                                autoComplete="name"
                                            />
                                        </div>
                                    </div>
                                )}

                                <div className="space-y-1">
                                    <label className="block text-[11px] font-mono font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400">
                                        Work Email Address
                                    </label>
                                    <div className="relative">
                                        <Mail
                                            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500"
                                            size={15}
                                        />
                                        <input
                                            type="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            className="w-full pl-9 pr-12 py-2 bg-slate-50/80 dark:bg-slate-950/70 hover:bg-slate-100/60 dark:hover:bg-slate-950/90 focus:bg-white dark:focus:bg-slate-950 border border-slate-200/80 dark:border-white/10 focus:border-[#005451] dark:focus:border-teal-500 focus:ring-2 focus:ring-[#005451]/15 dark:focus:ring-teal-500/20 rounded-xl text-slate-900 dark:text-white text-xs sm:text-sm placeholder:text-slate-400 dark:placeholder:text-slate-600 transition-all outline-none font-medium"
                                            placeholder="trader@quantdesk.com"
                                            disabled={isSigningIn || loading}
                                            autoComplete="email"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-1">
                                    <label className="block text-[11px] font-mono font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400">
                                        Security Password
                                    </label>
                                    <div className="relative">
                                        <Lock
                                            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500"
                                            size={15}
                                        />
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            className="w-full pl-9 pr-12 py-2 bg-slate-50/80 dark:bg-slate-950/70 hover:bg-slate-100/60 dark:hover:bg-slate-950/90 focus:bg-white dark:focus:bg-slate-950 border border-slate-200/80 dark:border-white/10 focus:border-[#005451] dark:focus:border-teal-500 focus:ring-2 focus:ring-[#005451]/15 dark:focus:ring-teal-500/20 rounded-xl text-slate-900 dark:text-white text-xs sm:text-sm placeholder:text-slate-400 dark:placeholder:text-slate-600 transition-all outline-none font-medium"
                                            placeholder="••••••••••••"
                                            disabled={isSigningIn || loading}
                                            minLength={6}
                                            autoComplete={isSignUp ? 'new-password' : 'current-password'}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors p-1"
                                        >
                                            {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                                        </button>
                                    </div>
                                </div>

                                {/* Luxury Primary Action Button */}
                                <button
                                    type="submit"
                                    disabled={isSigningIn || loading}
                                    className="w-full mt-2 py-2.5 px-5 rounded-xl bg-gradient-to-r from-[#005451] via-[#006e6a] to-[#005451] hover:from-[#004240] hover:via-[#005451] hover:to-[#004240] text-white font-bold text-xs sm:text-sm tracking-wide shadow-[0_6px_20px_-4px_rgba(0,84,81,0.35)] hover:shadow-[0_10px_25px_-4px_rgba(0,84,81,0.45)] hover:-translate-y-0.5 active:scale-[0.98] transition-all duration-300 flex items-center justify-center gap-2 overflow-hidden relative group disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {/* Shimmer sweep reflection on hover */}
                                    <div className="absolute inset-0 w-1/2 h-full bg-white/20 skew-x-12 -translate-x-full group-hover:translate-x-[350%] transition-transform duration-1000" />

                                    {isSigningIn || loading ? (
                                        <>
                                            <div className="w-3.5 h-3.5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                                            <span>Authorizing Access...</span>
                                        </>
                                    ) : (
                                        <>
                                            <span>{isSignUp ? 'Submit Clearance Request' : 'Authorize Terminal Access'}</span>
                                            <ArrowRight size={15} className="group-hover:translate-x-0.5 transition-transform" />
                                        </>
                                    )}
                                </button>
                            </form>

                            {/* Seamless Admin Clearance Assurance Strip (No Truncation) */}
                            <div className="pt-2.5 border-t border-slate-100 dark:border-white/5 flex items-center gap-2 text-[11px] text-slate-500 dark:text-slate-400 leading-tight">
                                <div className="w-4.5 h-4.5 rounded-full bg-emerald-50 dark:bg-emerald-950/50 border border-emerald-200/80 dark:border-emerald-800 text-[#005451] dark:text-emerald-400 flex items-center justify-center shrink-0">
                                    <ShieldCheck size={11} />
                                </div>
                                <span className="whitespace-normal leading-tight font-medium">
                                    {LOGIN_PAGE_CONFIG.FOOTER_ASSURANCE}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom Status Ribbon */}
            <div className="relative z-10 max-w-7xl mx-auto w-full pt-3 flex flex-col sm:flex-row items-center justify-between text-[10px] font-mono text-slate-400 dark:text-slate-500 gap-1">
                <span>{LOGIN_PAGE_CONFIG.BOTTOM_RIBBON_LEFT}</span>
                <span className="text-slate-500 dark:text-slate-400">{LOGIN_PAGE_CONFIG.BOTTOM_RIBBON_RIGHT}</span>
            </div>
        </div>
    )
}
